import type { AccountModel } from "@/api";
import type {
  DataGovernanceOverview,
  DataPlatformGovernance,
  EstateAsset,
} from "@/api/temp/TrustAIExecutiveService";
import governanceSnapshot from "./preprodGovernanceSnapshot.json";
import { snapshotAccounts } from "./snapshot";
import { sourceDisplayName } from "./sourcePresentation";

// The supplied aggregate snapshot supplements policy summary cards only.
// It does not provide per-platform policy counts or replace the 38-source inventory.
const { monitoringPolicyDetails, accessPolicyDetails } = governanceSnapshot;

export const PLATFORM_NAMES: Record<string, string> = {
  snowflake: "Snowflake",
  microsoft365: "Microsoft 365",
  databricks: "Databricks",
  dremio: "Dremio",
  mysql: "MySQL",
  powerbi: "Power BI",
  oracle: "Oracle",
  sqlserver: "SQL Server",
  aws: "Redshift",
  aurora: "Aurora",
  s3: "Amazon S3",
  dynamodb: "DynamoDB",
  emr: "Amazon EMR",
};

const completeCount = (
  rows: AccountModel[],
  key: "monitoringPolicyCount" | "dataAccessPolicyCount",
): number | null =>
  rows.every(row => row[key] !== undefined)
    ? rows.reduce((sum, row) => sum + row[key]!, 0)
    : null;

export const snapshotPlatforms = (): DataPlatformGovernance[] =>
  [...new Set(snapshotAccounts.map(row => row.type!))].map(type => {
    const rows = snapshotAccounts.filter(row => row.type === type);
    const active = rows.filter(row => row.accountStatus === "ACTIVE").length;
    return {
      id: type,
      name: PLATFORM_NAMES[type] ?? type,
      serviceType: type,
      dataSourceTotal: rows.length,
      securedCount: 0,
      incompleteCount: rows.length - active,
      snapshotSummary: {
        active,
        monitoringTotal: completeCount(rows, "monitoringPolicyCount"),
        accessTotal: completeCount(rows, "dataAccessPolicyCount"),
      },
      // Unknown breakdowns are withheld outright, now that the counts are
      // nullable — a zero read as a claim that there are none.
      monitoringPolicies: { total: null, enabled: null, disabled: null },
      accessControls: {
        total: null,
        deployed: null,
        saved: null,
        failed: null,
      },
      attentionItems: rows
        .filter(row => row.accountStatus !== "ACTIVE")
        .map(row => ({
          id: row.id!,
          name: sourceDisplayName(row),
          status: "NOT_EVALUATED" as const,
          details: [row.accountStatus!],
        })),
    };
  });

export const snapshotGovernanceOverview = (): DataGovernanceOverview => ({
  dimensions: [
    {
      id: "data-sources",
      total: snapshotAccounts.length,
      stats: [
        {
          id: "activeConnection",
          value: snapshotAccounts.filter(row => row.accountStatus === "ACTIVE")
            .length,
        },
        {
          id: "connectionAttention",
          value: snapshotAccounts.filter(row => row.accountStatus !== "ACTIVE")
            .length,
          attention: true,
        },
        { id: "notEvaluated", value: snapshotAccounts.length },
      ],
    },
    {
      id: "monitoring-policies",
      total: monitoringPolicyDetails.total,
      stats: [
        { id: "enabled", value: monitoringPolicyDetails.status.Enabled },
        {
          id: "disabled",
          value: monitoringPolicyDetails.status.Disabled,
          attention: true,
        },
      ],
    },
    {
      id: "access-controls",
      total: accessPolicyDetails.total,
      stats: [
        { id: "deployed", value: accessPolicyDetails.status.Deployed },
        { id: "saved", value: accessPolicyDetails.status.Saved },
        {
          id: "failed",
          value: accessPolicyDetails.status.Failed,
          attention: true,
        },
        { id: "published", value: accessPolicyDetails.status.Published },
        {
          id: "disabled",
          value: accessPolicyDetails.status.Disabled,
          attention: true,
        },
      ],
    },
  ],
});

export const snapshotEstateAssets = (): EstateAsset[] =>
  snapshotAccounts.map(row => ({
    id: row.id!,
    name: sourceDisplayName(row),
    type: "data-sources",
    owner: "Enterprise Data Platform",
    businessUnit: "Enterprise Data Platform",
    environment: "Production",
    status: "Not evaluated",
    platform: PLATFORM_NAMES[row.type!] ?? row.type!,
    platformId: row.type,
    description: `${row.accountStatus}. Governance not evaluated.`,
    violationCount: 0,
    runtimeActive: false,
  }));
